import { ChevronLeft, ChevronRight, MessageSquarePlus } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { BlockMarkdown } from '@/components/core/markdown/markdown';
import { VisualBlock } from '@/components/core/visual';
import type { Flashcard } from '@/data/fixtures';
import type { Grade } from '@/lib/practice/grades';
import { useHorizontalSwipe } from '@/hooks/ui/useHorizontalSwipe';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { requestChatPrefill } from '@/lib/chat-prefill';
import { PaginatedDots } from './PaginatedDots';
import { RestartButton } from './RestartButton';
import { PRACTICE_CARD } from './practiceSurface';

const RATINGS: { grade: Grade; label: string }[] = [
  { grade: 1, label: 'Again' },
  { grade: 2, label: 'Hard' },
  { grade: 3, label: 'Good' },
  { grade: 4, label: 'Easy' },
];

// The button matching the card's current rating is highlighted; everything
// else is neutral. Exactly one highlight, so a revisited card never looks like
// two ratings are selected.
const RATING_SELECTED_CLS =
  'border-brand-500 bg-brand-50 text-brand-700 dark:border-primary dark:tint-brand';
const RATING_NEUTRAL_CLS =
  'border-border bg-card text-foreground hover:border-brand-300 hover:bg-brand-50/50 dark:hover:border-primary dark:hover:bg-primary/10';

const FACE_CLS = cn(
  PRACTICE_CARD,
  'absolute inset-0 flex flex-col overflow-y-auto p-6 text-center [backface-visibility:hidden] sm:p-8'
);

export interface FlashcardDeckProps {
  card: Flashcard;
  index: number;
  total: number;
  revealed: boolean;
  rating: Grade | undefined;
  canGoNext: boolean;
  /** Whether anything is rated yet - restart only asks when it would lose some. */
  hasProgress: boolean;
  onFlip: () => void;
  onPrev: () => void;
  onNext: () => void;
  onRate: (grade: Grade) => void;
  onRestart: () => void;
}

/**
 * The flashcard deck as drawn. All state lives in `LessonFlashcardRunner`.
 *
 * - Prev / Next sit in the header beside the position, and a sideways swipe
 *   on the card does the same. As a row between the card and the ratings
 *   they put a mostly-disabled control in the way of the main action.
 * - The back repeats the question, small, above the answer: rating your
 *   recall without seeing what you were asked is guessing.
 * - "How well did you remember?" comes BEFORE the ratings it asks for, and
 *   "Ask about this card" offers the tutor once the answer is showing.
 * - The model's difficulty guess is a quiet "Difficulty · easy" in the
 *   header. On the card's corner it read "Easy" - the same word as the
 *   rating button under it.
 */
/**
 * A card with a picture can be taller than its face, which then scrolls: the
 * face shown is a named region a keyboard user can reach to scroll it, and
 * the face turned away leaves the tab order.
 */
function faceScroll(pictured: boolean, shown: boolean, label: string) {
  if (!pictured) return {};
  return {
    role: 'region' as const,
    'aria-label': label,
    tabIndex: shown ? 0 : -1,
  };
}

export function FlashcardDeck(props: FlashcardDeckProps) {
  const { card, revealed, rating, onFlip, onRate, onPrev, onNext } = props;
  const pictured = Boolean(card.frontVisual || card.backVisual);
  const touch = useMediaQuery('(max-width: 639.98px), (pointer: coarse)');
  const swipe = useHorizontalSwipe({
    onLeft: () => props.canGoNext && onNext(),
    onRight: () => props.index > 0 && onPrev(),
  });

  return (
    <div className='h-full overflow-y-auto p-4 sm:p-6'>
      <div className='mx-auto max-w-lg'>
        <DeckHeader {...props} />

        <div className='[perspective:1500px]' {...swipe}>
          {/* Two-face 3D flip: a preserve-3d flipper that rotates 180°,
              both faces rendered and backface-hidden so text never shows
              mirrored mid-flip. */}
          <div
            onClick={onFlip}
            className={cn(
              'relative min-h-72 w-full cursor-pointer [transform-style:preserve-3d] motion-safe:transition-transform motion-safe:duration-500 sm:min-h-80',
              // A card with a figure needs room for it above the words.
              pictured && 'min-h-[34rem] sm:min-h-[32rem]',
              revealed && '[transform:rotateY(180deg)]'
            )}
          >
            {/* The face turned away is `aria-hidden`: both faces are always in
                the DOM for the 3D flip, and a screen reader otherwise read the
                answer before the card was turned. */}
            <div
              aria-hidden={revealed}
              {...faceScroll(pictured, !revealed, 'Card front')}
              className={cn(FACE_CLS, 'items-center')}
            >
              {/* Centred by auto margins, not justify-center: a face taller
                  than the card scrolls from its top instead of losing it. */}
              <div className='my-auto flex w-full flex-col items-center'>
                {card.frontVisual && (
                  <VisualBlock
                    compact
                    caption={false}
                    visual={card.frontVisual}
                    className='mb-4 w-full shrink-0'
                  />
                )}
                <BlockMarkdown className='text-lg font-medium text-foreground sm:text-xl'>
                  {card.front}
                </BlockMarkdown>
                <p className='mt-6 text-xs text-brand-600 dark:text-primary'>
                  {touch ? 'Tap to reveal answer' : 'Click to reveal answer'}
                </p>
              </div>
            </div>
            <div
              aria-hidden={!revealed}
              {...faceScroll(pictured, revealed, 'Card back')}
              className={cn(FACE_CLS, '[transform:rotateY(180deg)]')}
            >
              {/* The question again, small, over the answer - only once the
                  card is turned, so the page never carries it twice before. */}
              {revealed && (
                <BlockMarkdown className='border-b border-border pb-3 text-sm text-muted-foreground'>
                  {card.front}
                </BlockMarkdown>
              )}
              <div className='flex flex-1 flex-col items-center justify-center gap-4 py-4'>
                {card.backVisual && (
                  <VisualBlock
                    compact
                    caption={false}
                    visual={card.backVisual}
                    className='w-full shrink-0'
                  />
                )}
                <BlockMarkdown className='text-base leading-relaxed text-foreground'>
                  {card.back}
                </BlockMarkdown>
              </div>
            </div>
          </div>
        </div>

        {revealed ? (
          <RatingArea card={card} rating={rating} onRate={onRate} />
        ) : (
          <p className='mt-4 text-center text-xs text-muted-foreground'>
            {touch
              ? 'Tap the card to flip · swipe to move'
              : 'Space or click to flip · ← → to move'}
          </p>
        )}
      </div>
    </div>
  );
}

function DeckHeader({
  card,
  index,
  total,
  canGoNext,
  hasProgress,
  onPrev,
  onNext,
  onRestart,
}: FlashcardDeckProps) {
  return (
    <div className='mb-3 flex items-center gap-1'>
      <NavButton direction='prev' disabled={index === 0} onClick={onPrev} />
      <span className='whitespace-nowrap font-mono text-sm tabular-nums text-muted-foreground'>
        {index + 1}/{total}
      </span>
      <NavButton
        direction='next'
        disabled={!canGoNext}
        title={
          !canGoNext && index < total - 1
            ? 'Rate this card to continue'
            : undefined
        }
        onClick={onNext}
      />
      <div className='flex flex-1 justify-center'>
        <PaginatedDots total={total} current={index} maxVisible={10} />
      </div>
      {card.difficulty && (
        <span className='mr-2 whitespace-nowrap font-mono text-xs text-muted-foreground'>
          Difficulty · {card.difficulty}
        </span>
      )}
      <RestartButton
        what='flashcards'
        hasProgress={hasProgress}
        onRestart={onRestart}
      />
    </div>
  );
}

function NavButton({
  direction,
  disabled,
  onClick,
  title,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
  title?: string;
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? 'Previous card' : 'Next card'}
      title={title}
      className='tap-target flex h-8 w-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:bg-transparent'
    >
      <Icon className='h-5 w-5' aria-hidden='true' />
    </button>
  );
}

/** The prompt, then Again / Hard / Good / Easy, then a way to the tutor. */
function RatingArea({
  card,
  rating,
  onRate,
}: {
  card: Flashcard;
  rating: Grade | undefined;
  onRate: (grade: Grade) => void;
}) {
  return (
    <div className='mt-4'>
      <p className='mb-2 text-center text-sm font-medium text-foreground'>
        How well did you remember?
      </p>
      <div className='grid grid-cols-4 gap-2'>
        {RATINGS.map(r => (
          <button
            key={r.grade}
            type='button'
            onClick={() => onRate(r.grade)}
            aria-pressed={rating === r.grade}
            className={cn(
              'min-h-11 rounded-lg border px-2 py-2 text-sm font-medium transition-colors sm:min-h-0',
              rating === r.grade ? RATING_SELECTED_CLS : RATING_NEUTRAL_CLS
            )}
          >
            {r.label}
          </button>
        ))}
      </div>
      <button
        type='button'
        onClick={() =>
          requestChatPrefill(`About the flashcard "${card.front}": `)
        }
        className='mx-auto mt-2 flex min-h-11 items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline dark:text-primary sm:min-h-8'
      >
        <MessageSquarePlus className='h-3.5 w-3.5' aria-hidden='true' />
        Ask about this card
      </button>
    </div>
  );
}
